import React from "react";
import { useDrop } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import { cn } from "@synthesis/shared-utils";

import { Line } from "./line";
import { Button, setSnack } from "@synthesis/shared-ui";
import { formatString, type NativeFileDropItem, type TargetBoxProps } from "../uploader.lib";

export const TargetBox = React.memo<TargetBoxProps>(
  ({ onDrop, isUploading, multiple, formats, onAbort, uploadProgress, indexOfUploadingFile, description }) => {
    const [{ canDrop, isOver }, drop] = useDrop(
      () => ({
        accept: [NativeTypes.FILE],
        drop(item: NativeFileDropItem) {
          for (const { name } of item.files) {
            const ext = name.split(".").at(-1)?.toLowerCase();
            if (formats?.length && ext && !formats.includes(ext)) {
              setSnack({ title: "Ошибка", description: "Неподходящий формат файла" });
              return;
            }
          }

          if (!multiple && item.files.length > 1) return;

          onDrop?.(item);
        },
        canDrop() {
          if (isUploading) return false;
          return true;
        },

        collect: (monitor: { isOver: () => boolean; canDrop: () => boolean }) => ({
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }),
      }),
      [isUploading, formats, multiple, onDrop],
    );

    const uploadInputRef = React.useRef<HTMLInputElement>(null);
    const setDropRef = React.useCallback(
      (node: HTMLElement | null) => {
        drop(node);
      },
      [drop],
    );

    const requiredFormats = React.useMemo(() => formats?.map((item) => `.${item},`).join(" "), [formats]);

    const message = React.useMemo(() => {
      if (description) {
        return description;
      }
      if (!formats || formats?.length > 4) {
        return `Перенесите сюда ${multiple ? "все файлы" : "файл"}`;
      }

      if (formats.length === 1) {
        return `Перенесите ${multiple ? "файлы" : "файл"} ${formats[0]}`;
      }

      return `Перенесите ${multiple ? "файлы" : "файл"} ${formatString(formats)}`;
    }, [formats, multiple, description]);

    const isDroping = canDrop && isOver;

    return (
      <section
        ref={setDropRef}
        className={cn(
          "border-neutrals-90 bg-neutrals-99 flex h-[10rem] items-center justify-center rounded-[0.875rem] border-2 border-dashed p-6 text-[#655C5C]",
          isDroping && "border-tertiary-70 bg-tertiary-95",
        )}
      >
        <input
          accept={requiredFormats || "*"}
          type="file"
          ref={uploadInputRef}
          hidden
          multiple={multiple}
          onChange={(e) => {
            const list = e.target.files;
            if (!list?.length) {
              return;
            }
            const files = Array.from(list);
            onDrop({ files });
            e.target.value = "";
          }}
        />
        {canDrop && isOver && <span>Перенесите сюда для загрузки</span>}
        {isUploading && (
          <div className="flex w-full flex-col items-center gap-4">
            <p>
              Загрузка файла...{" "}
              {indexOfUploadingFile.total > 1 && (
                <span>
                  {indexOfUploadingFile.current} из {indexOfUploadingFile.total}
                </span>
              )}
            </p>
            <Line uploadProgress={uploadProgress} />
            <Button onClick={onAbort}>Отменить</Button>
          </div>
        )}
        <div className="flex w-min flex-col items-center gap-2">
          {!isUploading && !isDroping && (
            <>
              <p className="whitespace-nowrap text-center text-sm">{message}</p>
              <div className="text-sm">или</div>
              <div className="w-max">
                <Button onClick={() => uploadInputRef.current?.click()}>Выберите {multiple ? "файлы" : "файл"} на устройстве</Button>
              </div>
            </>
          )}
        </div>
      </section>
    );
  },
);
